/**
 * SOPs page (platform users only). Lists the active SOPs from the
 * rebound-platform-docs checkout and renders the selected one's markdown.
 * SOPs flagged `contains_credentials` get a prominent warning banner + badge --
 * they're shown (operators need them) but the page makes the exposure explicit.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { useAuth } from "../contexts/AuthContext";
import { fetchSop, fetchSops, SopDoc, SopMeta } from "../services/sopsApi";

// Minimal markdown element styling (no @tailwindcss/typography dependency).
const MD_COMPONENTS = {
  h1: (p: any) => <h1 className="text-2xl font-bold mt-6 mb-3" {...p} />,
  h2: (p: any) => <h2 className="text-xl font-semibold mt-6 mb-2 border-b border-secondary-200 pb-1" {...p} />,
  h3: (p: any) => <h3 className="text-lg font-semibold mt-4 mb-2" {...p} />,
  p: (p: any) => <p className="my-2 leading-relaxed" {...p} />,
  ul: (p: any) => <ul className="list-disc ml-6 my-2 space-y-1" {...p} />,
  ol: (p: any) => <ol className="list-decimal ml-6 my-2 space-y-1" {...p} />,
  a: (p: any) => <a className="text-blue-600 underline break-words" target="_blank" rel="noreferrer" {...p} />,
  code: ({ inline, ...p }: any) =>
    inline ? (
      <code className="bg-secondary-100 rounded px-1 py-0.5 text-sm font-mono" {...p} />
    ) : (
      <code className="font-mono text-sm" {...p} />
    ),
  pre: (p: any) => (
    <pre className="bg-secondary-900 text-secondary-50 rounded-lg p-3 my-3 overflow-x-auto text-sm" {...p} />
  ),
  table: (p: any) => (
    <div className="overflow-x-auto my-3">
      <table className="min-w-full border border-secondary-200 text-sm" {...p} />
    </div>
  ),
  th: (p: any) => <th className="border border-secondary-200 bg-secondary-50 px-2 py-1 text-left font-semibold" {...p} />,
  td: (p: any) => <td className="border border-secondary-200 px-2 py-1 align-top" {...p} />,
  blockquote: (p: any) => <blockquote className="border-l-4 border-secondary-300 pl-3 italic my-3" {...p} />,
};

function CredBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium px-2 py-0.5">
      🔑 credentials
    </span>
  );
}

const SopsPage: React.FC = () => {
  const { token } = useAuth();
  const [list, setList] = useState<SopMeta[] | null>(null);
  const [synced, setSynced] = useState<boolean>(true);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [doc, setDoc] = useState<SopDoc | null>(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    fetchSops(token)
      .then((r) => {
        setList(r.sops);
        setSynced(r.synced !== false);
      })
      .catch((e) => setListError(e instanceof Error ? e.message : String(e)));
  }, [token]);

  // Ignore stale/out-of-order responses: clicking two SOPs quickly must not
  // render the earlier doc under the later selection (see ProductPicker).
  const reqIdRef = useRef(0);

  const open = useCallback(
    (id: string) => {
      if (!token) return;
      const reqId = ++reqIdRef.current;
      setSelectedId(id);
      setDoc(null);
      setDocError(null);
      setDocLoading(true);
      fetchSop(id, token)
        .then((d) => { if (reqId === reqIdRef.current) setDoc(d); })
        .catch((e) => { if (reqId === reqIdRef.current) setDocError(e instanceof Error ? e.message : String(e)); })
        .finally(() => { if (reqId === reqIdRef.current) setDocLoading(false); });
    },
    [token],
  );

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-2xl font-bold mb-1">SOPs</h1>
      <p className="text-secondary-500 text-sm mb-4">
        Standard operating procedures from the platform docs. Updated daily.
      </p>

      {listError && (
        <div className="rounded-lg bg-red-50 text-red-700 px-3 py-2 mb-4 text-sm">
          Couldn&apos;t load SOPs: {listError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        {/* List */}
        <div className="border border-secondary-200 rounded-lg divide-y divide-secondary-100 self-start">
          {list === null && !listError && (
            <div className="p-3 text-secondary-500 text-sm">Loading…</div>
          )}
          {list !== null && list.length === 0 && synced && (
            <div className="p-3 text-secondary-500 text-sm">No SOPs published yet.</div>
          )}
          {list !== null && list.length === 0 && !synced && (
            <div className="p-3 text-amber-700 text-sm">
              The SOP docs haven&apos;t synced on this server yet (the docs-repo access
              token isn&apos;t configured). Once set, SOPs appear here automatically.
            </div>
          )}
          {list?.map((m) => (
            <button
              key={m.id}
              onClick={() => open(m.id)}
              className={`w-full text-left px-3 py-2 hover:bg-secondary-50 ${
                selectedId === m.id ? "bg-blue-50" : ""
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm">{m.title}</span>
                {m.contains_credentials && <CredBadge />}
              </div>
              <div className="text-xs text-secondary-400 mt-0.5">
                {m.id}
                {m.domain ? ` · ${m.domain}` : ""}
                {m.last_verified ? ` · verified ${m.last_verified}` : ""}
              </div>
            </button>
          ))}
        </div>

        {/* Viewer */}
        <div className="min-w-0">
          {!selectedId && (
            <div className="text-secondary-400 text-sm p-3">Select an SOP to read it.</div>
          )}
          {docLoading && <div className="text-secondary-500 text-sm p-3">Loading…</div>}
          {docError && (
            <div className="rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">
              Couldn&apos;t load this SOP: {docError}
            </div>
          )}
          {doc && (
            <article className="border border-secondary-200 rounded-lg p-4 md:p-6 min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h2 className="text-xl font-bold">{doc.title}</h2>
                {doc.contains_credentials && <CredBadge />}
              </div>
              <div className="text-xs text-secondary-400 mb-3">
                {doc.id}
                {doc.owner ? ` · owner ${doc.owner}` : ""}
                {doc.last_verified ? ` · verified ${doc.last_verified}` : ""}
              </div>
              {doc.contains_credentials && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 mb-4 text-sm">
                  ⚠️ This SOP contains credentials. Treat this page as sensitive — don&apos;t
                  screen-share or paste it outside the platform team.
                </div>
              )}
              <div className="min-w-0">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                  {doc.body}
                </ReactMarkdown>
              </div>
            </article>
          )}
        </div>
      </div>
    </div>
  );
};

export default SopsPage;
